import { isSponsorCategory, type SponsorSegment } from './model.ts';

/**
 * The wire format, measured against the live service (2026-08-01) rather than assumed:
 *
 * - `GET {base}/api/skipSegments/{sha256-prefix}` answers a **bucket** — an array of
 *   `{ videoID, segments: [...] }` for every video whose id hashes into the prefix (~150 for a
 *   4-hex prefix), already filtered by the `categories` parameter.
 * - "No segments" is a **404 with the plain-text body `Not Found`** — not a `200 []`. The caller
 *   maps that status to "no segments" before this parser ever runs; an empty bucket for a
 *   category filter *is* `200 []`, so both spellings mean the same nothing.
 * - A segment row is `{ category, actionType, segment: [startSec, endSec], UUID, votes, … }`.
 *   `actionType` is `skip` for everything Visious asked for, but the check stays: a `mute` or
 *   `full` row means a different behaviour, and skipping those ranges would be wrong, not strict.
 *
 * Total (R4): a malformed row drops itself; a malformed payload answers an empty list. A missing
 * video is not an error — it is the normal case for ~149 of the ~150 bucket entries.
 */
export function parseSponsorBlockBucket(payload: unknown, videoId: string): readonly SponsorSegment[] {
  if (!Array.isArray(payload)) return [];
  for (const entry of payload) {
    if (typeof entry !== 'object' || entry === null) continue;
    const record = entry as Readonly<Record<string, unknown>>;
    if (record['videoID'] !== videoId) continue;
    return parseSegmentRows(record['segments']);
  }
  return [];
}

function parseSegmentRows(rows: unknown): readonly SponsorSegment[] {
  if (!Array.isArray(rows)) return [];
  const segments: SponsorSegment[] = [];
  for (const row of rows) {
    if (typeof row !== 'object' || row === null) continue;
    const record = row as Readonly<Record<string, unknown>>;
    if (record['actionType'] !== 'skip') continue;
    const category = record['category'];
    if (!isSponsorCategory(category)) continue;

    const range = record['segment'];
    if (!Array.isArray(range) || range.length < 2) continue;
    const start = range[0];
    const end = range[1];
    if (typeof start !== 'number' || !Number.isFinite(start) || start < 0) continue;
    if (typeof end !== 'number' || !Number.isFinite(end) || end <= start) continue;

    const uuid = record['UUID'];
    segments.push({
      // The UUID keys suppression ("don't re-skip what I un-skipped"), so a row without one gets a
      // synthetic key rather than being dropped — the range is still real.
      uuid: typeof uuid === 'string' && uuid !== '' ? uuid : `${category}:${String(start)}`,
      category,
      startSec: start,
      endSec: end,
    });
  }
  // The service answers in submission order, not timeline order; every consumer wants the timeline.
  return segments.sort((a, b) => a.startSec - b.startSec);
}
